import React, { Fragment, useEffect, useState } from 'react';
import { Col, Card, CardHeader, Table, Input, Row, Badge, ModalFooter } from 'reactstrap';
import { Btn } from '../../AbstractElements';
import CommonModal from '../../Components/Modals/modal';
import Pagination from '../../Components/Pagination/Pagination';
import Empty from '../../Components/Empty/Index';
import { useDispatch, useSelector } from 'react-redux';
import { getTeacherApplications, updateTeacherApplicationStatus, setSelectedApplication } from '../../store/teacherApplicationsSlice';
import moment from 'moment';

const TeacherApplicationsTable = () => {
  const dispatch = useDispatch();
  const { applicationsData, totalApplications, selectedApplication } = useSelector((state) => state.teacherApplications);

  const [keyword, setKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 20;

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [typingTimer, setTypingTimer] = useState(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchApplications = (page = 1, search = keyword, start = startDate, end = endDate) => {
    const offset = (page - 1) * limit;
    dispatch(getTeacherApplications(limit, offset, search, start, end));
  };

  useEffect(() => {
    fetchApplications(1, '', '', '');
  }, []);

  const pageChange = (page) => {
    setCurrentPage(page);
    fetchApplications(page, keyword, startDate, endDate);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setKeyword(val);
    clearTimeout(typingTimer);
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchApplications(1, val, startDate, endDate);
    }, 600);
    setTypingTimer(timer);
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
    fetchApplications(1, keyword, start, end);
  };

  const handleResetFilters = () => {
    setKeyword('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    fetchApplications(1, '', '', '');
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      const { service } = await import('../../shared/_services/api_service');
      const response = await service.exportTeacherApplications(keyword, startDate, endDate);
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Teacher_Applications_${moment().format('YYYYMMDD_HHmm')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export CSV:', error);
      alert('Failed to export CSV from server');
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenDetail = (app) => {
    dispatch(setSelectedApplication(app));
    setDetailModalOpen(true);
  };

  const handleStatusChange = (id, newStatus) => {
    dispatch(updateTeacherApplicationStatus(id, newStatus));
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return <Badge color="success">ACCEPTED</Badge>;
      case 'rejected':
        return <Badge color="danger">REJECTED</Badge>;
      case 'reviewed':
        return <Badge color="info">REVIEWED</Badge>;
      default:
        return <Badge color="warning">PENDING</Badge>;
    }
  };

  return (
    <Fragment>
      <Col sm='12'>
        <Card>
          <CardHeader>
            <Row className="gy-3 align-items-center">
              <Col md="4">
                <Input
                  className="form-control"
                  placeholder='Search by name, email, phone or city...'
                  type="text"
                  value={keyword}
                  onChange={handleSearchChange}
                />
              </Col>
              <Col md="5" className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center gap-1 w-50">
                  <span className="text-muted text-xs">From:</span>
                  <Input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => handleDateChange(e.target.value, endDate)}
                  />
                </div>
                <div className="d-flex align-items-center gap-1 w-50">
                  <span className="text-muted text-xs">To:</span>
                  <Input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => handleDateChange(startDate, e.target.value)}
                  />
                </div>
                {(startDate || endDate || keyword) && (
                  <Btn attrBtn={{ color: 'light', size: 'sm', onClick: handleResetFilters }}>
                    Reset
                  </Btn>
                )}
              </Col>
              <Col md="3" className="text-end">
                <Btn attrBtn={{ color: 'success', size: 'sm', onClick: handleExportCSV, disabled: isExporting }}>
                  <i className={`fa ${isExporting ? 'fa-spinner fa-spin' : 'fa-download'} me-1`}></i>
                  {isExporting ? 'Exporting...' : 'Export CSV'}
                </Btn>
              </Col>
            </Row>
          </CardHeader>

          <div className='table-responsive'>
            <Table hover={true} className='table-border-horizontal table-light'>
              <thead>
                <tr>
                  <th scope='col'>Sl.No</th>
                  <th scope='col'>Name</th>
                  <th scope='col'>Contact</th>
                  <th scope='col'>Qualification</th>
                  <th scope='col'>Experience</th>
                  <th scope='col'>Applied On</th>
                  <th scope='col'>Status</th>
                  <th scope='col'>Action</th>
                </tr>
              </thead>
              <tbody>
                {applicationsData.map((item, index) => (
                  <tr key={item._id}>
                    <th scope='row'>{(currentPage - 1) * limit + index + 1}</th>
                    <td>
                      <div className="font-bold">{item.salutation} {item.name}</div>
                      <div className="text-muted text-xs">{item.city}</div>
                    </td>
                    <td>
                      <div>{item.email}</div>
                      <div className="text-xs text-muted">Ph: {item.phone}</div>
                    </td>
                    <td>
                      <div>{item.highestDegree}</div>
                      {item.fieldOfStudy && <div className="text-xs text-muted">{item.fieldOfStudy}</div>}
                    </td>
                    <td>{item.yearsExperience}</td>
                    <td>{moment(item.createdAt).format('DD MMM YYYY')}</td>
                    <td>{renderStatusBadge(item.status)}</td>
                    <td>
                      <div className='d-flex gap-2 align-items-center'>
                        <div className='cursor-pointer bg-light-primary font-primary action-icon p-1 rounded' onClick={() => handleOpenDetail(item)}>
                          <i className="fa fa-eye fa-lg"></i>
                          <div className="tooltipCustom">View Details</div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {totalApplications <= 0 && <Empty />}
        </Card>

        {totalApplications > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItem={totalApplications}
            itemsPerPage={limit}
            showEllipsisAfter={true}
            visiblePageCount={3}
            onPageChange={pageChange}
          />
        )}
      </Col>

      {/* Details Modal */}
      <CommonModal
        isOpen={detailModalOpen}
        title={`Application Details: ${selectedApplication?.salutation || ''} ${selectedApplication?.name || ''}`}
        toggler={() => setDetailModalOpen(false)}
        size={'lg'}
      >
        {selectedApplication && (
          <div className="p-3 space-y-4">
            <Row className="gy-3">
              <Col md="6">
                <strong>Salutation & Name:</strong>
                <div>{selectedApplication.salutation} {selectedApplication.name}</div>
              </Col>
              <Col md="6">
                <strong>City:</strong>
                <div>{selectedApplication.city}</div>
              </Col>
              <Col md="6">
                <strong>Email:</strong>
                <div>{selectedApplication.email}</div>
              </Col>
              <Col md="6">
                <strong>Phone & WhatsApp:</strong>
                <div>Ph: {selectedApplication.phone} | WA: {selectedApplication.whatsapp}</div>
              </Col>
            </Row>

            <hr />

            <Row className="gy-3">
              <Col md="6">
                <strong>Highest Qualification:</strong>
                <div>{selectedApplication.highestDegree}</div>
              </Col>
              <Col md="6">
                <strong>Field of Study:</strong>
                <div>{selectedApplication.fieldOfStudy || 'N/A'}</div>
              </Col>
              <Col md="6">
                <strong>Teaching Certificate / License:</strong>
                <div>{selectedApplication.teachingCertificate || 'N/A'}</div>
              </Col>
              <Col md="6">
                <strong>Years of Experience:</strong>
                <div>{selectedApplication.yearsExperience}</div>
              </Col>
            </Row>

            <hr />

            <div>
              <strong className="text-dark">Why do you want to teach at O2 Academia & BODMAS?</strong>
              <p className="bg-light text-dark p-3 rounded border mt-1 text-sm font-medium">{selectedApplication.whyTeach}</p>
            </div>

            <div>
              <strong className="text-dark">Demo Video / Presentation YouTube URL:</strong>
              <div className="mt-1">
                {selectedApplication.youtubeUrl ? (
                  <a href={selectedApplication.youtubeUrl} target="_blank" rel="noopener noreferrer" className="text-primary font-bold">
                    <i className="fa fa-youtube-play text-danger me-1"></i> {selectedApplication.youtubeUrl}
                  </a>
                ) : 'N/A'}
              </div>
            </div>

            {selectedApplication.teachingMethod && (
              <div>
                <strong className="text-dark">Teaching Methods / Approach:</strong>
                <p className="bg-light text-dark p-3 rounded border mt-1 text-sm font-medium">{selectedApplication.teachingMethod}</p>
              </div>
            )}

            <hr />

            <div>
              <strong className="text-dark">Subjects You Can Teach:</strong>
              <div className="d-flex flex-wrap gap-1 mt-2">
                {selectedApplication.subjects?.map((sub, i) => (
                  <Badge key={i} color="primary" className="me-1 mb-1 px-2 py-1 text-white font-semibold">{sub}</Badge>
                ))}
              </div>
            </div>

            <Row className="gy-3 mt-1">
              <Col md="6">
                <strong>Available Days:</strong>
                <div>{selectedApplication.availableDays?.join(', ') || 'N/A'}</div>
              </Col>
              <Col md="6">
                <strong>Preferred Class Time:</strong>
                <div>{selectedApplication.preferredClassTime || 'N/A'}</div>
              </Col>
              <Col md="6">
                <strong>Hours Available Per Week:</strong>
                <div>{selectedApplication.hoursAvailablePerWeek || 'N/A'}</div>
              </Col>
              <Col md="6">
                <strong>Current Application Status:</strong>
                <div className="mt-1">{renderStatusBadge(selectedApplication.status)}</div>
              </Col>
            </Row>

            <div className="mt-4 p-3 bg-light rounded border d-flex flex-column align-items-center justify-content-center gap-2 text-center">
              <span className="font-bold text-dark">Update Status</span>
              <div className="d-flex gap-2 justify-content-center">
                <Btn attrBtn={{ color: 'info', size: 'sm', onClick: () => handleStatusChange(selectedApplication._id, 'reviewed') }}>Set Reviewed</Btn>
                <Btn attrBtn={{ color: 'success', size: 'sm', onClick: () => handleStatusChange(selectedApplication._id, 'accepted') }}>Set Accepted</Btn>
                <Btn attrBtn={{ color: 'danger', size: 'sm', onClick: () => handleStatusChange(selectedApplication._id, 'rejected') }}>Set Rejected</Btn>
              </div>
            </div>
          </div>
        )}
        <ModalFooter>
          <Btn attrBtn={{ color: 'secondary', onClick: () => setDetailModalOpen(false) }}>Close</Btn>
        </ModalFooter>
      </CommonModal>
    </Fragment>
  );
};

export default TeacherApplicationsTable;
